import type { ApiError as ApiErrorBody } from '@cc/api-types';

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Anything a request body can be besides JSON: a form with files. */
export type UploadBody = { form: FormData };

export interface TransportResponse {
  status: number;
  data: unknown;
}

/** Sends one request and returns its status and parsed body. The real one uses fetch; mock mode swaps in src/mock. */
export type Transport = (method: Method, path: string, body: unknown, signal?: AbortSignal) => Promise<TransportResponse>;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(status: number, message: string, code: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export function isUpload(body: unknown): body is UploadBody {
  return typeof body === 'object' && body !== null && (body as { form?: unknown }).form instanceof FormData;
}

const fetchTransport: Transport = async (method, path, body, signal) => {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = { method, credentials: 'include', headers, signal: signal ?? null };
  if (isUpload(body)) {
    // The browser sets the multipart Content-Type, with its boundary, itself.
    init.body = body.form;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch (err) {
    if (signal?.aborted) throw err;
    return { status: 0, data: null };
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  return { status: res.status, data };
};

let transportPromise: Promise<Transport> | null = null;

function transport(): Promise<Transport> {
  transportPromise ??= __MOCK__ ? import('../mock/server').then((m) => m.mockTransport) : Promise.resolve(fetchTransport);
  return transportPromise;
}

/**
 * Endpoints that answer 401 for their own reasons (a wrong code, a guide link that expired)
 * must never bounce anyone to the login page.
 */
const PUBLIC_PREFIXES = ['/api/auth/', '/api/g/'];

let unauthorizedHandler: () => void = () => {
  if (window.location.pathname !== '/login') window.location.assign('/login');
};

/** The router registers a soft redirect here so a lapsed session lands on /login without a reload. */
export function setUnauthorizedHandler(fn: () => void) {
  unauthorizedHandler = fn;
}

function isErrorBody(data: unknown): data is ApiErrorBody {
  return typeof data === 'object' && data !== null && typeof (data as { message?: unknown }).message === 'string';
}

function fallbackMessage(status: number): string {
  if (status === 0) return 'No connection right now. Check the internet and try again.';
  if (status === 401) return 'Please sign in again.';
  if (status === 404) return 'That is not here any more.';
  if (status === 409) return 'Someone already took care of this.';
  if (status === 429) return 'Too many tries. Wait a minute and try again.';
  if (status >= 500) return 'Something went wrong on our side. Try again in a moment.';
  return 'That did not work. Try again.';
}

/** Send files as multipart/form-data. The body is `{ form }`; mock mode reads the file names from it. */
export function upload<T>(path: string, form: FormData): Promise<T> {
  return request<T>('POST', path, { form } satisfies UploadBody);
}

export async function request<T>(method: Method, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const send = await transport();
  const { status, data } = await send(method, path, body, signal);
  if (status >= 200 && status < 300) {
    // Every endpoint answers JSON. A page instead (an older server, a proxy) is an error, not data.
    if (typeof data === 'string') throw new ApiError(502, 'The server sent a page instead of data. Try again in a moment.', 'not_json');
    return data as T;
  }
  if (status === 401 && !PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix))) unauthorizedHandler();
  const message = isErrorBody(data) && data.message ? data.message : fallbackMessage(status);
  const code = isErrorBody(data) ? data.error : null;
  throw new ApiError(status, message, code);
}
